import { Footer } from '@/widgets/footer';
import { Navbar } from '@/widgets/navbar';
import { Ban, Flag, Gavel, Mail, ShieldAlert, UserCheck } from 'lucide-react';

// Page publique exigée par la politique « Child Safety Standards » de Google Play
// (applis de rencontre / réseaux sociaux) : normes contre l'exploitation et les
// abus sexuels sur mineurs (CSAE) + point de contact.
const CONTACT_EMAIL = 'info@zawajme.com';
const LAST_UPDATE = '30 octobre 2026';

const sections = [
  {
    icon: Ban,
    title: 'Tolérance zéro',
    paragraphs: [
      "Zawajme interdit strictement toute forme d'exploitation et d'abus sexuels sur mineurs (CSAE, Child Sexual Abuse and Exploitation). Cela comprend notamment :",
    ],
    items: [
      "les contenus d'abus sexuels sur mineurs (CSAM), quelle que soit leur forme (photo, vidéo, texte, dessin, image générée) ;",
      'la sollicitation ou la manipulation de mineurs à des fins sexuelles (grooming) ;',
      'la sextorsion et toute forme de chantage visant un mineur ;',
      'la traite de mineurs et toute proposition de mariage impliquant un mineur ;',
      'tout contenu ou message qui sexualise un mineur.',
    ],
  },
  {
    icon: UserCheck,
    title: 'Un service réservé aux adultes',
    paragraphs: [
      "Zawajme est une plateforme de rencontre en vue du mariage, réservée aux personnes âgées d'au moins 18 ans. La date de naissance est demandée à l'inscription et les profils comme les photos sont contrôlés par notre équipe de modération.",
      "Tout compte appartenant à un mineur, ou utilisé pour entrer en contact avec un mineur, est supprimé dès qu'il est identifié.",
    ],
  },
  {
    icon: ShieldAlert,
    title: 'Nos mesures',
    paragraphs: [
      'Dès que nous avons connaissance d’un contenu ou d’un comportement relevant de la CSAE :',
    ],
    items: [
      'le contenu est retiré immédiatement ;',
      "le compte concerné est suspendu puis supprimé, et l'utilisateur ne peut plus accéder au service ;",
      'les éléments nécessaires à l’enquête sont conservés, dans le respect de la loi ;',
      'les faits sont signalés aux autorités compétentes (voir ci-dessous).',
    ],
  },
  {
    icon: Gavel,
    title: 'Signalement aux autorités',
    paragraphs: [
      "Zawajme respecte les lois applicables en matière de protection de l'enfance. Tout contenu d'abus sexuels sur mineurs avéré est signalé aux autorités compétentes, notamment :",
    ],
    items: [
      'en France : la plateforme PHAROS (internet-signalement.gouv.fr) ;',
      'en Belgique : Child Focus (childfocus.be) et la police ;',
      'au niveau international : le NCMEC (National Center for Missing & Exploited Children, CyberTipline).',
    ],
  },
];

export default function ChildSafetyPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        {/* Header */}
        <section className="py-16 md:py-24 border-b border-border bg-gradient-to-b from-primary-surface/50 to-background">
          <div className="container mx-auto max-w-3xl px-4">
            <div className="h-14 w-14 rounded-xl bg-primary-surface flex items-center justify-center mb-6">
              <ShieldAlert className="h-7 w-7 text-primary" />
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
              Normes de protection de l&apos;enfance
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Les présentes normes s&apos;appliquent à l&apos;application mobile Zawajme et au site
              www.zawajme.com. Elles décrivent comment nous luttons contre l&apos;exploitation et
              les abus sexuels sur mineurs.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              Dernière mise à jour : {LAST_UPDATE}
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="container mx-auto max-w-3xl px-4 space-y-8">
            {sections.map((section) => (
              <div
                key={section.title}
                className="p-6 md:p-8 rounded-xl border border-border bg-card"
              >
                <div className="flex items-center gap-3 mb-4">
                  <section.icon className="h-6 w-6 text-primary shrink-0" />
                  <h2 className="font-display text-xl md:text-2xl font-semibold">
                    {section.title}
                  </h2>
                </div>
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-muted-foreground leading-relaxed mb-3 last:mb-0"
                  >
                    {paragraph}
                  </p>
                ))}
                {section.items && (
                  <ul className="mt-2 space-y-2 list-disc pl-5 text-muted-foreground leading-relaxed">
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Report + point of contact */}
            <div className="p-6 md:p-8 rounded-xl border border-primary/30 bg-primary-surface">
              <div className="flex items-center gap-3 mb-4">
                <Flag className="h-6 w-6 text-primary shrink-0" />
                <h2 className="font-display text-xl md:text-2xl font-semibold">
                  Signaler un contenu ou nous contacter
                </h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Si vous constatez sur Zawajme un contenu ou un comportement mettant en danger un
                mineur, écrivez à notre point de contact protection de l&apos;enfance, en indiquant
                le pseudo du profil concerné. Les signalements sont traités en priorité.
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Protection de l'enfance")}`}
                className="mt-4 inline-flex items-center gap-2 font-semibold text-primary hover:underline"
              >
                <Mail className="h-5 w-5" />
                {CONTACT_EMAIL}
              </a>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Si un enfant est en danger immédiat, contactez sans attendre la police ou les
                services d&apos;urgence (112). En France, vous pouvez aussi appeler le 119 (Allô
                Enfance en Danger) ; en Belgique, le 116 000 (Child Focus).
              </p>
            </div>

            {/* English summary for store reviewers and non-French speakers */}
            <div lang="en" className="p-6 md:p-8 rounded-xl border border-border bg-card">
              <h2 className="font-display text-xl md:text-2xl font-semibold mb-4">
                Child Safety Standards (English)
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Zawajme (mobile app and www.zawajme.com) has zero tolerance for child sexual abuse
                  and exploitation (CSAE), including child sexual abuse material (CSAM), grooming,
                  sextortion and trafficking. The service is restricted to adults aged 18 and over.
                </p>
                <p>
                  When we become aware of CSAE content or behaviour, we remove the content, ban the
                  account, preserve evidence as required by law and report confirmed CSAM to the
                  relevant authorities (PHAROS in France, Child Focus in Belgium, NCMEC).
                </p>
                <p>
                  Child safety point of contact:{' '}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="font-semibold text-primary hover:underline"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
